/**
 * Unified polling hook for OMEN Web Tracker.
 *
 * Main cycle  — runs every `prefs.pollingIntervalSec` seconds:
 *   For each selected flight: fetchMessages + fetchTeamPositions + fetchPredictions
 *
 * Slow cycle — runs every 5 minutes (fixed):
 *   fetchSeparators (refreshes available IMEI list in store)
 *
 * Reads store state via `useAppStore.getState()` inside callbacks so intervals
 * always see the latest selection, even after it changes between ticks.
 *
 * The Axios 401 interceptor (App.tsx) handles session expiry — errors from
 * individual fetch calls are swallowed here to keep the cycle alive.
 */

import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppStore } from '../store/appStore';
import type { Message, Prediction, Separator, TeamPosition } from '../types/api';

/** Hours to extend the message window beyond a flight's end_time. */
const ADD_HOURS = 2;

/** Slow cycle interval in milliseconds (5 minutes, fixed). */
const SLOW_CYCLE_MS = 5 * 60 * 1000;

// ── Individual fetchers ────────────────────────────────────────────────────────

/**
 * Fetches new separator messages for `flightId` and appends them to the store.
 * Uses the `since` parameter (last known timestamp) for incremental updates.
 * The server wraps the array in `{ separator_messages: [...] }`.
 * @param flightId - target flight ID
 */
async function fetchMessages(flightId: number): Promise<void> {
  const { messages, setMessages } = useAppStore.getState();
  const last = messages[flightId]?.at(-1);

  const { data } = await axios.get<{ separator_messages: Message[] }>(
    `/flights/${flightId}/separator-messages`,
    {
      params: {
        since: last?.timestamp ?? undefined,
        add: ADD_HOURS,
      },
    },
  );

  const msgs = data.separator_messages;
  if (msgs && msgs.length > 0) setMessages(flightId, msgs);
}

/**
 * Fetches the latest team positions for `flightId` and updates the store.
 * The server wraps the array in `{ team: [...] }`.
 * @param flightId - target flight ID
 */
async function fetchTeamPositions(flightId: number): Promise<void> {
  const { setTeams } = useAppStore.getState();
  const { data } = await axios.get<{ team: TeamPosition[] }>(
    `/flights/${flightId}/team-positions`,
  );
  if (data.team) setTeams(flightId, data.team);
}

/**
 * Fetches normal and instant predictions for `flightId`.
 * Skipped entirely if both prediction types are disabled in per-flight prefs.
 * Exported so FlightTab can trigger an immediate one-off fetch when a prediction
 * toggle is turned on (without waiting for the next polling cycle).
 * @param flightId - target flight ID
 */
export async function fetchPredictions(flightId: number): Promise<void> {
  const { flightPrefs, setPrediction } = useAppStore.getState();
  const fp = flightPrefs[flightId];

  if (fp && !fp.showNormal && !fp.showInstant) return;

  const [normalResult, instantResult] = await Promise.allSettled([
    axios.get<Prediction>(`/flights/${flightId}/predictions`, {
      params: { type: 'normal' },
    }),
    axios.get<Prediction>(`/flights/${flightId}/predictions`, {
      params: { type: 'instant' },
    }),
  ]);

  if (normalResult.status === 'fulfilled') {
    setPrediction(flightId, 'normal', normalResult.value.data);
  }
  if (instantResult.status === 'fulfilled') {
    setPrediction(flightId, 'instant', instantResult.value.data);
  }
}

/**
 * Fetches the separator IMEI list and updates `store.separators`.
 * The server wraps the array in `{ imeis: [...] }`.
 */
async function fetchSeparators(): Promise<void> {
  const { setSeparators } = useAppStore.getState();
  const { data } = await axios.get<{ imeis: Separator[] }>('/imeis', {
    params: { type: 'separator' },
  });
  if (data.imeis) setSeparators(data.imeis);
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface UsePollingResult {
  /** Run an immediate cycle and start periodic polling. Safe to call while already polling. */
  startPolling: () => void;
  /** Stop all polling intervals. */
  stopPolling: () => void;
}

/**
 * Manages the main and slow polling cycles.
 * @returns `{ startPolling, stopPolling }`
 */
export function usePolling(): UsePollingResult {
  const mainIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const slowIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Tracks whether polling is currently active. */
  const activeRef = useRef(false);
  const isTourDemo = useAppStore((s) => s.isTourDemo);

  // ── Cycle runners ───────────────────────────────────────────────────────────

  const runMainCycle = useCallback(async () => {
    const { selectedFlightIds, setPollingState } = useAppStore.getState();
    setPollingState(true, new Date());

    await Promise.all(
      selectedFlightIds.map((id) =>
        Promise.all([
          fetchMessages(id).catch(() => undefined),
          fetchTeamPositions(id).catch(() => undefined),
          fetchPredictions(id).catch(() => undefined),
        ]),
      ),
    );
  }, []);

  const runSlowCycle = useCallback(async () => {
    await fetchSeparators().catch(() => undefined);
  }, []);

  // ── Start / stop ────────────────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (mainIntervalRef.current !== null) {
      clearInterval(mainIntervalRef.current);
      mainIntervalRef.current = null;
    }
    if (slowIntervalRef.current !== null) {
      clearInterval(slowIntervalRef.current);
      slowIntervalRef.current = null;
    }
    activeRef.current = false;
    useAppStore.getState().setPollingState(false);
  }, []);

  const startPolling = useCallback(() => {
    // Do not start polling in tour demo mode
    if (isTourDemo) return;
    // Clear any existing intervals before re-starting
    stopPolling();
    activeRef.current = true;

    const { prefs } = useAppStore.getState();
    const intervalMs = prefs.pollingIntervalSec * 1000;

    // Run immediately, then on schedule
    void runMainCycle();
    void runSlowCycle();

    mainIntervalRef.current = setInterval(() => void runMainCycle(), intervalMs);
    slowIntervalRef.current = setInterval(() => void runSlowCycle(), SLOW_CYCLE_MS);
  }, [runMainCycle, runSlowCycle, stopPolling]);

  // ── Restart when pollingIntervalSec changes while active ────────────────────

  useEffect(() => {
    const unsub = useAppStore.subscribe((state, prev) => {
      if (
        activeRef.current &&
        state.prefs.pollingIntervalSec !== prev.prefs.pollingIntervalSec
      ) {
        startPolling();
      }
    });
    return unsub;
  }, [startPolling]);

  // ── Cleanup on unmount ──────────────────────────────────────────────────────

  useEffect(() => stopPolling, [stopPolling]);

  return { startPolling, stopPolling };
}
